import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Globe, Clock, Shield, Users } from 'lucide-react';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: 'Private Charter',
      description: 'Exclusive aircraft charter for business or pleasure.',
      path: '/services/private-charter',
      icon: <Shield className="w-6 h-6" />,
    },
    {
      title: 'Group Charter',
      description: 'High capacity aircraft solutions for corporate groups, sports teams and special events.',
      path: '/services/group-charter',
      icon: <Users className="w-6 h-6" />,
    },
    {
      title: 'Air Cargo',
      description: 'Time-critical, cost critical, oversized, hazardous, or sensitive cargo solutions.',
      path: '/services/air-cargo',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Special Missions',
      description: 'Specialized aviation operations including government support, humanitarian aid and unique mission requirements.',
      path: '/services/special-missions',
      icon: <Shield className="w-6 h-6" />,
    },
    {
      title: 'On-Demand Fleet',
      description: 'Access to 500+ aircraft worldwide, from light jets to wide-body airliners.',
      path: '/fleet',
      icon: <Clock className="w-6 h-6" />,
    },
    {
      title: 'Empty Legs',
      description: 'Exceptional value on repositioning flights. Premium travel at reduced rates when available.',
      path: '/services/empty-legs',
      icon: <ArrowRight className="w-6 h-6" />,
    },
  ];

  const stats = [
    { value: '15+', label: 'Years Serving Singapore' },
    { value: '24/7', label: 'Global Support' },
    { value: '500+', label: 'Worldwide Aircraft Access' },
    { value: '100%', label: 'Government & Corporate Trusted' },
  ];

  return (
    <div data-testid="home-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-start overflow-hidden">
        {/* Background Image - Desktop */}
        <div className="absolute inset-0 hidden md:block">
          <img
            src="/Updatebanner01.jpg"
            alt="Aircraft flying over Singapore"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent"></div>
        </div>
        {/* Background Image - Mobile */}
        <div className="absolute inset-0 md:hidden">
          <img
            src="/Updatemobilebanner.jpg"
            alt="Aircraft flying over Singapore"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-block text-white text-sm font-medium tracking-widest uppercase mb-6"
            >
              Singapore's Premier Aviation Partner
            </motion.span>
            
            <h1 
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-7xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Private Aviation Solutions Without Compromise
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed mb-10 max-w-2xl">
              Executive jet charter to group travel, cargo and special missions. Proudly Singapore based. Operating worldwide.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-enquiry-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Charter Enquiry
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+6581888023"
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/20 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-white/60 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* About Positioning Section */}
      <section data-testid="about-section" className="section-gap bg-slate-50">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="caption-text text-blue-700 mb-4 block">About Us</span>
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Aviation Expertise Since 2010
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                Founded by a management team of experienced Singapore based aviation professionals, Singapore Air Charter provides tailored aviation solutions across private charter, group charter, cargo and special operations.
              </p>
              <p className="body-default mb-8">
                Independent, detail-focused and globally connected. We leverage our deep industry expertise and worldwide network to deliver seamless aviation experiences.
              </p>
              <Link
                to="/about"
                data-testid="about-learn-more-btn"
                className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-blue-700 transition-colors"
              >
                Learn More About Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/hp003.jpg"
                alt="Private jet wing view"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-white p-8 shadow-float hidden md:block">
                <span className="text-4xl font-semibold text-blue-700" style={{ fontFamily: 'Playfair Display, serif' }}>15+</span>
                <p className="text-sm text-slate-600 mt-1">Years of Excellence</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="container-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <span 
                  className="block text-3xl md:text-4xl font-semibold text-white mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {stat.value}
                </span>
                <span className="text-sm text-slate-400 font-light">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section-gap">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">What We Offer</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Aviation Services
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={service.path}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="card-feature block h-full group"
                >
                  <div className="text-blue-700 mb-6 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 
                    className="text-xl font-medium text-slate-900 mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm text-blue-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Preview Section */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.aircharterservice.com/global/aircraft-guide/private-charter/gulfstream-g150-1.jpg"
                  alt="Gulfstream G150"
                  className="w-full h-64 object-cover"
                />
                <img
                  src="https://worldwidelux.com/2.0/wp-content/uploads/2025/03/world_wide_lux_fleet_embraer_phenom-300_1.jpg"
                  alt="Phenom 300ER"
                  className="w-full h-64 object-cover"
                />
                <img
                  src="https://cdn.privatejetcardcomparisons.com/uploads/Gulfstream-G550-pric1e.jpg"
                  alt="Gulfstream G550"
                  className="w-full h-64 object-cover col-span-2"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="caption-text text-blue-700 mb-4 block">Our Fleet</span>
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                World-Class Aircraft
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                Access to over 500 aircraft worldwide, from light jets to wide-body airliners.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 bg-red-600"></div>
                  Gulfstream Series (G100 to G650ER)
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 bg-red-600"></div>
                  Bombardier Global & Challenger Range
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 bg-red-600"></div>
                  Embraer, Hawker & Citation Aircraft
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 bg-red-600"></div>
                  Large Aircraft (Boeing 737)
                </li>
              </ul>
              <Link
                to="/fleet"
                data-testid="fleet-explore-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300"
              >
                Explore Our Fleet
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559628233-100c798642d4?w=1920&q=85"
            alt="Private jet at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>
        
        <div className="relative z-10 container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-5xl font-medium text-white mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Ready to Elevate Your Travel?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              Contact our team for a personalized charter quote. Available 24/7 for your aviation needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-enquiry-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Get a Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+6581888023"
                data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/10 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                +65 8188 8023
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
